// ── 매출 관리 공통 스타일 + 유틸 ──────────────────────────────────────────────
// 2026-08 디자인 통일: 직원 관리 표준(adminlayout/designStandard.js) 토큰 값으로 수렴.
//   · 표: 흰 카드 + 1px 테두리 + 그림자 없음 + 검정 머리글 띠 + 마지막 행 밑줄 제거
//   · 버튼: 검정 박스(주요) / 흰 외곽선(보조) — 파랑 primary 제거
import { toDateString } from '../../../utils/date';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';

/** 숫자를 세 자리 콤마 표기로 변환 */
export function fmtNum(v) {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('ko-KR');
}

/** 날짜 문자열 표시 */
export function fmtDate(v) { return v ? toDateString(v) : '-'; }

// ── 테이블 스타일 — stdTableWrap/stdTh/stdTd 값 (넓은 표라 가로 스크롤은 유지) ──
export const tblWrap  = { overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'none' };
export const thStyle  = { background: 'var(--std-thead, #0f172a)', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.92)', borderBottom: 'none', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' };
export const tdStyle  = { padding: '10px 12px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--bg-subtle)', whiteSpace: 'nowrap', verticalAlign: 'middle' };
export const amtTd    = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

// 마지막 행 밑줄 제거(stdCell 패턴) — n행 중 i행의 셀 3종(일반/금액/버튼) 선택 헬퍼
export const rowCells = (i, n) => {
  const base = i === n - 1 ? { ...tdStyle, borderBottom: 'none' } : tdStyle;
  return {
    td:  base,
    amt: { ...base, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    btn: { ...base, textAlign: 'right' },
  };
};

// ── 버튼 스타일 — 검정 CTA / 흰 외곽선 (직원 관리 표준) ────────────────────────
export const btnPrimary   = { border: '1px solid var(--std-btn, #0f172a)', borderRadius: 12, padding: '8px 14px', background: 'var(--std-btn, #0f172a)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
export const btnSecondary = { border: '1px solid var(--border)', borderRadius: 12, padding: '8px 14px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
// 표 안 행 액션 버튼 — 기준 화면(직원 관리)과 같은 컴팩트 아웃라인 밀도
export const btnEdit    = { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', cursor: 'pointer' };
export const btnDanger  = { ...btnEdit, color: '#dc2626' };
// 수정/삭제 버튼 셀 — 오른쪽 정렬, 내용 줄바꿈 방지
export const btnTd      = { ...tdStyle, whiteSpace: 'nowrap', textAlign: 'right' };
// 버튼을 담는 flex 컨테이너 — 오른쪽 정렬
export const btnGroup   = { display: 'flex', gap: 4, justifyContent: 'flex-end' };

// ── 필터 바 스타일 ─────────────────────────────────────────────────────────────
export const filterWrap  = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 };
// background/color 명시 — 다크모드에서 브라우저 기본값(밝은 배경)으로 텍스트가 안 보이는 문제 방지
export const inputStyle  = { fontSize: 12, padding: '5px 9px', border: '1px solid var(--border)', borderRadius: 6, outline: 'none', background: 'var(--bg-card)', color: 'var(--text)' };
export const selectStyle = { ...inputStyle };

// ── 모달 공통 스타일 (떠 있는 요소 — 그림자 유지) ──────────────────────────────
export const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
export const modalBox    = {
  background: 'var(--bg-card)', borderRadius: 10, padding: 24, minWidth: 320, maxWidth: 480,
  width: '95vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
// 모달 헤더 — 검정 밴드 표준. modalBox 는 padding 24 카드라 음수 마진으로 가장자리까지 확장
//   (IncentiveRuleModal.styles.js 와 같은 패턴, 라운드는 modalBox 10 에 맞춤)
export const modalTitle     = { ...stdModalHeaderBar(10), margin: '-24px -24px 16px' };
export const modalIconBox   = stdModalHeaderIconBox;
export const modalTitleText = stdModalHeaderTitle;
export const modalCloseBtn  = stdModalCloseBtn;
export const fieldRow    = { marginBottom: 12 };
export const labelTxt    = { fontSize: 12, color: 'var(--text-sub)', marginBottom: 4, display: 'block', fontWeight: 600 };
export const fullInput   = { ...inputStyle, width: '100%', boxSizing: 'border-box' };
export const fullSelect  = { ...selectStyle, width: '100%', boxSizing: 'border-box' };
export const modalBtns   = { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 };
export const btnCancel   = { fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', cursor: 'pointer' };
export const btnSave     = { ...btnPrimary };
